<template>
  <div v-if="showSkeleton" class="flex flex-col">
    <LcSkeleton height="1.875rem" radius="medium" width="60%" />
    <LcSkeleton class="mt-200 mb-100" height="1.625rem" radius="medium" width="40%" />
    <LcSkeleton class="mb-050" height="0.75rem" radius="medium" />
    <LcSkeleton class="mb-050" height="0.75rem" radius="medium" />
    <LcSkeleton class="mb-050" height="0.75rem" radius="medium" width="40%" />
  </div>

  <div v-else-if="content.length" class="editor-rules-content">
    <h3 v-if="title" class="paragraph-bold">{{ title }}</h3>

    <template v-for="item in content" :key="item?.sys.id">
      <h4 v-if="item.caption" class="paragraph-bold mt-200 mb-100">
        {{ item.caption }}
      </h4>

      <ContentfulRenderer class="content" :data="item.content" />
    </template>
  </div>
</template>

<script lang="ts" setup>
import type { HighFrequencyGameId } from '@lottocom/high-frequency-game-mapping'

import type {
  HighFrequency_GameId,
  LotteryId,
} from '#api/shared/@types/generated/backend/graphql-schema-types'
import type { ContentBlock } from '#content/shared/@types/generated/cms/graphql-schema-types'

type Emits = {
  (e: 'error'): void
}

type Props = {
  lotteryId: HighFrequency_GameId | HighFrequencyGameId | LotteryId
}

const emit = defineEmits<Emits>()
const { lotteryId } = defineProps<Props>()

const { data, error, pending } = useContent().getRules(lotteryId)

const showSkeleton = computed(() => import.meta.client && pending.value)

const content = computed(() => (data.value?.contentBlocksCollection?.items as ContentBlock[]) || [])
const title = computed(() => data.value?.title)

watch(error, () => emit('error'))
</script>

<style lang="scss" scoped>
.editor-rules-content {
  :deep(.content) {
    h3,
    h4,
    h5,
    h6 {
      @include token-paragraph-bold;

      margin-bottom: token('size/space/gap/100');
    }

    dl,
    ol,
    ul {
      margin-bottom: token('size/space/gap/200');
    }

    ol,
    ul {
      padding-left: token('size/space/gap/200');
      list-style: unset;

      ::marker {
        color: token('color/text/accent/accent/placeholder');
      }
    }

    p {
      @include token-caption-regular;
    }
  }
}
</style>
